const PERMISSION_STATUS = 403
const REFERENCE_VALIDATION_ERROR_PREFIX = 'Invalid reference for field '

const STATUS_KEYS = ['status', 'statusCode'] as const
const NESTED_ERROR_KEYS = [
  'body',
  'cause',
  'data',
  'error',
  'errors',
  'extensions',
  'networkError',
  'originalError',
  'response',
  'result',
] as const

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function getTrimmedString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null
  }

  const trimmed = value.trim()

  return trimmed.length > 0 ? trimmed : null
}

function readNestedErrorValue<T>(
  value: unknown,
  extract: (record: Record<string, unknown>) => T | null,
  seen: WeakSet<object> = new WeakSet(),
): T | null {
  if (Array.isArray(value)) {
    for (const item of value) {
      const nestedItem = readNestedErrorValue(item, extract, seen)

      if (nestedItem !== null) {
        return nestedItem
      }
    }

    return null
  }

  if (!isRecord(value) || seen.has(value)) {
    return null
  }

  seen.add(value)

  const directValue = extract(value)

  if (directValue !== null) {
    return directValue
  }

  for (const key of NESTED_ERROR_KEYS) {
    const nestedValue = readNestedErrorValue(value[key], extract, seen)

    if (nestedValue !== null) {
      return nestedValue
    }
  }

  return null
}

function readPermissionStatus(value: unknown): number | null {
  return readNestedErrorValue(value, (record) => {
    for (const key of STATUS_KEYS) {
      if (record[key] === PERMISSION_STATUS) {
        return PERMISSION_STATUS
      }
    }

    return null
  })
}

function readMessage(value: unknown): string | null {
  if (typeof value === 'string') {
    return getTrimmedString(value)
  }

  if (value instanceof Error) {
    const errorMessage = getTrimmedString(value.message)

    if (errorMessage) {
      return errorMessage
    }
  }

  return readNestedErrorValue(value, (record) =>
    getTrimmedString(record.message),
  )
}

function formatReferenceValidationMessage(message: string): string {
  if (!message.startsWith(REFERENCE_VALIDATION_ERROR_PREFIX)) {
    return message
  }

  const separatorIndex = message.indexOf(': ')

  if (separatorIndex === -1) {
    return 'A reference field points to a missing or invalid record.'
  }

  const referenceId = message.slice(separatorIndex + 2).trim()

  if (!referenceId) {
    return 'A reference field points to a missing or invalid record.'
  }

  return `Reference value "${referenceId}" must point to an existing record in the linked collection.`
}

export function isPermissionError(error: unknown): boolean {
  return readPermissionStatus(error) === PERMISSION_STATUS
}

export function getUserFacingErrorMessage(
  error: unknown,
  fallback: string,
): string {
  const message = readMessage(error) ?? fallback

  return formatReferenceValidationMessage(message)
}

export function resolveActionError(error: unknown, fallback: string) {
  return {
    isPermissionError: isPermissionError(error),
    message: getUserFacingErrorMessage(error, fallback),
  }
}
